import { partition } from 'es-toolkit';
import paper from 'paper';

import {
  Annotation,
  AnnotationSharedWith,
  ImageAnnotation,
  ImageAnnotationWithoutComputedProperties,
  ShapeAnnotation,
  ShapeType,
  TextAnnotation,
  TextAnnotationWithoutComputedProperties,
} from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import {
  imageAnnotationDefaultsFactory,
  shapeAnnotationDefaultsFactory,
  textAnnotationDefaultsFactory,
} from './annotationUtilities';
import {
  getBoundingBoxFromItem,
  getBoundingBoxFromWidthHeightAndCenterPoint,
  getPositionInPaperCoordinates,
  pageImageCoordinatesToPdfCoordinates,
  PageImageInfo,
  pixelsToPoints,
} from './paperItemTranslator';

function needsModernizing(
  annotationObjectFromDatabase: Annotation | PaperItem,
): annotationObjectFromDatabase is PaperItem {
  return Array.isArray(annotationObjectFromDatabase);
}

export default async function modernizeAnnotations(
  annotations: readonly (Annotation | PaperItem)[],
  pageImageInfo: PageImageInfo,
) {
  const [annotationsToModernize, alreadyModernAnnotations] = partition(annotations, needsModernizing);

  if (annotationsToModernize.length === 0) {
    return alreadyModernAnnotations;
  }

  const modernizedAnnotations = await Promise.all(
    annotationsToModernize.map((annotation) => convertOldFormatAnnotation(annotation, pageImageInfo)),
  );

  // Filter out annotations that could not be parsed or those with unimplemented conversions
  const filteredModernizedAnnotations = modernizedAnnotations.filter(notNullish);

  return alreadyModernAnnotations.concat(filteredModernizedAnnotations);
}

interface PaperRaster {
  readonly 0: 'Raster';
  readonly 1: { readonly source: string; readonly selected?: boolean };
  readonly 2: {
    readonly centerPositionInPdfCoordinates: { readonly 1: number; readonly 2: number };
    readonly userEmail: string;
  };
}
interface PaperPointText {
  readonly 0: 'PointText';
  readonly 1: {
    readonly content: string;
    readonly fillColor?: {
      readonly 0: number;
      readonly 1: number;
      readonly 2: number;
    };
    readonly selected?: boolean;
  };
  readonly 2: {
    readonly fontSizeInPoints: number;
    readonly bottomLeftPositionInRasterCoordinates: { readonly 1: number; readonly 2: number };
    readonly userEmail: string;
    readonly sharedWith: AnnotationSharedWith;
  };
  readonly rotation: number;
  visible: boolean;
}
interface PaperPath {
  readonly 0: 'Path';
}
interface PaperGroup {
  readonly 0: 'Group';
}
interface PaperShape {
  readonly 0: 'Shape';
  readonly 1: {
    readonly type: ShapeType;
    readonly size: { readonly 0: number; readonly 1: number };
    readonly fillColor: { readonly 0: number; readonly 1: number; readonly 2: number };
    readonly selected?: boolean;
  };
  readonly 2: {
    readonly centerPositionInPdfCoordinates: { readonly 1: number; readonly 2: number };
    readonly userEmail: string;
  };
}

type PaperItem = PaperRaster | PaperPointText | PaperPath | PaperGroup | PaperShape;

// Currently there are only two styles of annotations in the database: those that follow the current schema, and the
//   old "paper item json" annotations.  This function can be used to convert an old annotation into a modern one.
function convertOldFormatAnnotation(
  paperItemJson: PaperItem,
  pageImageInfo: PageImageInfo,
): Promise<Annotation | void> {
  const paperItemType = paperItemJson[0];
  switch (paperItemType) {
    case 'Raster':
      return convertOldFormatRasterAnnotation(paperItemJson as PaperRaster, pageImageInfo);

    case 'PointText':
      return Promise.resolve(convertOldFormatTextAnnotation(paperItemJson as PaperPointText, pageImageInfo));

    case 'Path':
      console.log('Path: Conversion to annotation not yet implemented');
      break; // TODO

    case 'Group':
      console.log('Group: Conversion to annotation not yet implemented');
      break; // TODO

    case 'Shape':
      return Promise.resolve(convertOldFormatShapeAnnotation(paperItemJson as PaperShape, pageImageInfo.dpi));

    default:
      console.log('Error converting paper item JSON to annotation');
      console.log(paperItemJson);
  }

  return Promise.resolve(undefined);
}

function convertOldFormatTextAnnotation(
  paperPointTextJson: PaperPointText,
  pageImageInfo: PageImageInfo,
  zIndex = 0,
): TextAnnotation {
  const text = paperPointTextJson[1].content;

  let color;
  if (paperPointTextJson[1].fillColor == null) color = '#000';
  else color = rgbColorArrayToHexString(paperPointTextJson[1].fillColor);

  const fontSize = paperPointTextJson[2].fontSizeInPoints;
  const leading = fontSize;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const paperPointTextForCalculation = new paper.PointText();
  paperPointTextForCalculation.importJSON(paperPointTextJson);
  paperPointTextJson.visible = false;

  const delta = {
    x: paperPointTextForCalculation.bounds.width / 2,
    y: paperPointTextForCalculation.bounds.height / 2,
  };

  const bottomLeftPositionInPageImageCoordinates = {
    x: paperPointTextJson[2].bottomLeftPositionInRasterCoordinates[1],
    y: paperPointTextJson[2].bottomLeftPositionInRasterCoordinates[2],
  };

  const centerPositionInPageImageCoordinates = {
    x: bottomLeftPositionInPageImageCoordinates.x + delta.x,
    y: bottomLeftPositionInPageImageCoordinates.y - delta.y,
  };

  const position = pageImageCoordinatesToPdfCoordinates(centerPositionInPageImageCoordinates, pageImageInfo);

  const { selected } = paperPointTextJson[1];
  const creatorEmail = paperPointTextJson[2].userEmail;
  const { sharedWith } = paperPointTextJson[2];
  const { rotation } = paperPointTextJson;

  const textAnnotation: TextAnnotationWithoutComputedProperties = {
    ...textAnnotationDefaultsFactory(),
    contents: text,
    color,
    fontSize,
    leading,
    fontFamily: 'sans-serif',
    sharedWith,
    position,
    boundingBox: null,
    zIndex,
    creatorEmail,
    rotation,
    selected: selected ?? false,
  };

  paperPointTextForCalculation.position = getPositionInPaperCoordinates(textAnnotation.position!, pageImageInfo);
  const boundingBox = getBoundingBoxFromItem(paperPointTextForCalculation, pageImageInfo);
  paperPointTextForCalculation.remove();

  return {
    ...textAnnotationDefaultsFactory(),
    contents: text,
    color,
    fontSize,
    leading,
    fontFamily: 'sans-serif',
    sharedWith,
    position,
    boundingBox,
    zIndex,
    creatorEmail,
    rotation,
    selected: selected ?? false,
  };
}

function convertOldFormatRasterAnnotation(paperRasterJson: PaperRaster, pageImageInfo: PageImageInfo, zIndex = 0) {
  return new Promise<ImageAnnotation>((resolve) => {
    const { source } = paperRasterJson[1];

    const position = {
      x: pixelsToPoints(paperRasterJson[2].centerPositionInPdfCoordinates[1], pageImageInfo.dpi),
      y: pixelsToPoints(paperRasterJson[2].centerPositionInPdfCoordinates[2], pageImageInfo.dpi),
    };

    const { selected } = paperRasterJson[1];
    const creatorEmail = paperRasterJson[2].userEmail;

    const mockedPaperRaster = new paper.Raster();
    mockedPaperRaster.importJSON(paperRasterJson as unknown as string);
    mockedPaperRaster.visible = false;

    const scale = {
      x: mockedPaperRaster.matrix.scaling.x,
      y: mockedPaperRaster.matrix.scaling.y,
    };

    const { rotation } = mockedPaperRaster;

    const imageAnnotation: ImageAnnotationWithoutComputedProperties = {
      ...imageAnnotationDefaultsFactory(),
      source,
      position,
      boundingBox: null,
      zIndex,
      creatorEmail,
      scale,
      rotation,
      selected: selected ?? false,
    };

    mockedPaperRaster.position = getPositionInPaperCoordinates(imageAnnotation.position!, pageImageInfo);

    mockedPaperRaster.onLoad = () => {
      const boundingBox = getBoundingBoxFromItem(mockedPaperRaster, pageImageInfo);
      mockedPaperRaster.remove();

      const result: ImageAnnotation = {
        ...imageAnnotationDefaultsFactory(),
        source,
        position,
        boundingBox,
        zIndex,
        creatorEmail,
        scale,
        rotation,
        selected: selected ?? false,
      };

      resolve(result);
    };
  });
}

function convertOldFormatShapeAnnotation(
  paperShapeJson: PaperShape,
  pageImageDpi: number,
  zIndex = 0,
): ShapeAnnotation {
  const shapeType = paperShapeJson[1].type;

  if (shapeType !== 'rectangle') {
    console.error(`Warning: invalid shape type encountered: '${shapeType}'`);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const mockedPaperShape = new paper.Shape.Rectangle();
  mockedPaperShape.importJSON(paperShapeJson);
  mockedPaperShape.visible = false;
  const scaling = {
    x: mockedPaperShape.matrix.scaling.x,
    y: mockedPaperShape.matrix.scaling.y,
  };
  const { rotation } = mockedPaperShape;
  mockedPaperShape.remove();

  const width = pixelsToPoints(paperShapeJson[1].size[0] * scaling.x, pageImageDpi);
  const height = pixelsToPoints(paperShapeJson[1].size[1] * scaling.y, pageImageDpi);
  const shapeGeometryProperties = { width, height };

  const color = rgbColorArrayToHexString(paperShapeJson[1].fillColor);

  const position = {
    x: pixelsToPoints(paperShapeJson[2].centerPositionInPdfCoordinates[1], pageImageDpi),
    y: pixelsToPoints(paperShapeJson[2].centerPositionInPdfCoordinates[2], pageImageDpi),
  };

  const boundingBox = getBoundingBoxFromWidthHeightAndCenterPoint(
    shapeGeometryProperties.width,
    shapeGeometryProperties.height,
    position,
  );

  const { selected } = paperShapeJson[1];
  const creatorEmail = paperShapeJson[2].userEmail;

  return {
    ...shapeAnnotationDefaultsFactory(),
    shapeType,
    width: shapeGeometryProperties.width,
    height: shapeGeometryProperties.height,
    color,
    position,
    boundingBox,
    zIndex,
    creatorEmail,
    rotation,
    selected: selected ?? false,
  };
}

function rgbColorArrayToHexString(rgbColorArray: { readonly 0: number; readonly 1: number; readonly 2: number }) {
  let redComponent = Math.round(rgbColorArray[0] * 255).toString(16);
  let greenComponent = Math.round(rgbColorArray[1] * 255).toString(16);
  let blueComponent = Math.round(rgbColorArray[2] * 255).toString(16);
  if (redComponent.length === 1) redComponent = `0${redComponent}`;
  if (greenComponent.length === 1) greenComponent = `0${greenComponent}`;
  if (blueComponent.length === 1) blueComponent = `0${blueComponent}`;
  return `#${redComponent}${greenComponent}${blueComponent}`;
}
